import {
  AlertTriangle,
  Building2,
  Gauge,
  Grape,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Wine as WineIcon,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import { LocationSelect } from "../components/LocationSelect";
import { Modal } from "../components/Modal";
import { EmptyState, ErrorState, PageLoader } from "../components/States";
import { useToast } from "../components/Toast";
import { api, OFFLINE_DATA_CHANGED_EVENT } from "../lib/api";
import {
  buildCellarOccupancy,
  type CellarOccupancyGroup,
} from "../lib/cellar-occupancy";
import { bottleLabel } from "../lib/wine";
import type { CellarLocation, CellarPlaceRecord } from "../types";

type EditorMode = "create" | "edit" | null;
const UNASSIGNED_PLACE = "__unassigned__";

export function LocationsPage() {
  const [places, setPlaces] = useState<CellarPlaceRecord[]>([]);
  const [locations, setLocations] = useState<CellarLocation[]>([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [placeEditor, setPlaceEditor] = useState<EditorMode>(null);
  const [locationEditor, setLocationEditor] = useState<EditorMode>(null);
  const [placeToEdit, setPlaceToEdit] = useState<CellarPlaceRecord | null>(null);
  const [placeToDelete, setPlaceToDelete] = useState<CellarPlaceRecord | null>(null);
  const [locationToEdit, setLocationToEdit] = useState<CellarLocation | null>(null);
  const [locationToDelete, setLocationToDelete] = useState<CellarLocation | null>(null);
  const { showToast } = useToast();

  const load = useCallback(async (preferredPlaceId?: string) => {
    setLoading(true);
    setError("");
    try {
      const [placeValues, locationValues] = await Promise.all([
        api.listPlaces(),
        api.listLocations(),
      ]);
      setPlaces(placeValues);
      setLocations(locationValues);
      const knownPlaceIds = new Set(placeValues.map((place) => place.id));
      const hasUnassigned = locationValues.some(
        (location) =>
          !location.lieu_id || !knownPlaceIds.has(location.lieu_id),
      );
      setSelectedPlaceId((current) => {
        const requested = preferredPlaceId ?? current;
        if (placeValues.some((place) => place.id === requested)) return requested;
        if (requested === UNASSIGNED_PLACE && hasUnassigned) return requested;
        return placeValues[0]?.id ?? (hasUnassigned ? UNASSIGNED_PLACE : "");
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const refreshAfterSynchronization = () => void load();
    window.addEventListener(
      OFFLINE_DATA_CHANGED_EVENT,
      refreshAfterSynchronization,
    );
    return () =>
      window.removeEventListener(
        OFFLINE_DATA_CHANGED_EVENT,
        refreshAfterSynchronization,
      );
  }, [load]);

  const selectedPlace = useMemo(
    () => places.find((place) => place.id === selectedPlaceId) ?? null,
    [places, selectedPlaceId],
  );
  const unassignedLocations = useMemo(
    () => {
      const knownPlaceIds = new Set(places.map((place) => place.id));
      return locations.filter(
        (location) =>
          !location.lieu_id || !knownPlaceIds.has(location.lieu_id),
      );
    },
    [locations, places],
  );
  const visibleLocations = useMemo(
    () =>
      selectedPlaceId === UNASSIGNED_PLACE
        ? unassignedLocations
        : locations.filter((location) => location.lieu_id === selectedPlaceId),
    [locations, selectedPlaceId, unassignedLocations],
  );
  const occupancyGroups = useMemo(
    () => buildCellarOccupancy(places, locations),
    [locations, places],
  );

  const openPlaceCreate = () => {
    setPlaceToEdit(null);
    setPlaceEditor("create");
  };

  const openPlaceEdit = (place: CellarPlaceRecord) => {
    setPlaceToEdit(place);
    setPlaceEditor("edit");
  };

  const openLocationCreate = () => {
    setLocationToEdit(null);
    setLocationEditor("create");
  };

  const openLocationEdit = (location: CellarLocation) => {
    setLocationToEdit(location);
    setLocationEditor("edit");
  };

  return (
    <AppShell>
      <div className="page locations-page">
        <header className="locations-header">
          <div>
            <p className="eyebrow">Organisation de la cave</p>
            <h1>Gérer les emplacements</h1>
            <p>Créez vos lieux, puis organisez précisément les bouteilles qui s’y trouvent.</p>
          </div>
          <button className="button button-primary" type="button" onClick={openPlaceCreate}>
            <Plus size={18} aria-hidden="true" /> Ajouter un lieu
          </button>
        </header>

        {loading ? <PageLoader label="Chargement des lieux et emplacements…" /> : null}
        {!loading && error ? <ErrorState message={error} onRetry={() => void load()} /> : null}

        {!loading && !error ? (
          <div className="place-location-workspace">
            <section className="places-management" aria-labelledby="places-title">
              <div className="management-heading">
                <div>
                  <p className="management-step">1</p>
                  <div>
                    <h2 id="places-title">Lieux</h2>
                    <p>Choisissez le lieu dont vous voulez organiser les emplacements.</p>
                  </div>
                </div>
                <button className="button button-secondary" type="button" onClick={openPlaceCreate}>
                  <Plus size={17} aria-hidden="true" /> Ajouter
                </button>
              </div>

              {places.length === 0 && unassignedLocations.length === 0 ? (
                <EmptyState
                  icon={<Building2 size={27} aria-hidden="true" />}
                  title="Aucun lieu"
                  message="Ajoutez votre premier lieu, par exemple Orsay ou Houlgate."
                  action={
                    <button className="button button-primary" type="button" onClick={openPlaceCreate}>
                      <Plus size={18} aria-hidden="true" /> Ajouter un lieu
                    </button>
                  }
                />
              ) : (
                <div className="place-list" role="list" aria-label="Lieux disponibles">
                  {places.map((place) => (
                    <article
                      className={`place-card ${selectedPlaceId === place.id ? "selected" : ""}`}
                      key={place.id}
                      role="listitem"
                    >
                      <button
                        className="place-select"
                        type="button"
                        aria-pressed={selectedPlaceId === place.id}
                        onClick={() => setSelectedPlaceId(place.id)}
                      >
                        <span className="place-mark"><Building2 size={18} aria-hidden="true" /></span>
                        <span>
                          <strong>{place.nom}</strong>
                          <small>
                            {place.nombre_emplacements} emplacement{place.nombre_emplacements === 1 ? "" : "s"}
                            {" · "}{bottleLabel(place.stock)}
                          </small>
                        </span>
                      </button>
                      <div className="place-actions">
                        <button type="button" onClick={() => openPlaceEdit(place)} aria-label={`Renommer ${place.nom}`}>
                          <Pencil size={16} aria-hidden="true" />
                        </button>
                        <button type="button" onClick={() => setPlaceToDelete(place)} aria-label={`Supprimer ${place.nom}`}>
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  ))}
                  {unassignedLocations.length > 0 ? (
                    <article
                      className={`place-card place-card-unassigned ${selectedPlaceId === UNASSIGNED_PLACE ? "selected" : ""}`}
                      role="listitem"
                    >
                      <button
                        className="place-select"
                        type="button"
                        aria-pressed={selectedPlaceId === UNASSIGNED_PLACE}
                        onClick={() => setSelectedPlaceId(UNASSIGNED_PLACE)}
                      >
                        <span className="place-mark place-unknown"><AlertTriangle size={18} aria-hidden="true" /></span>
                        <span>
                          <strong>Lieu à préciser</strong>
                          <small>{unassignedLocations.length} emplacement{unassignedLocations.length === 1 ? "" : "s"} à réaffecter</small>
                        </span>
                      </button>
                    </article>
                  ) : null}
                </div>
              )}
            </section>

            {(selectedPlace || selectedPlaceId === UNASSIGNED_PLACE) ? (
              <section className="locations-management" aria-labelledby="locations-title">
                <div className="management-heading">
                  <div>
                    <p className="management-step">2</p>
                    <div>
                      <h2 id="locations-title">Emplacements</h2>
                      <p>
                        {selectedPlace
                          ? `Repères disponibles à ${selectedPlace.nom}.`
                          : "Ces anciens emplacements doivent être rattachés à un lieu."}
                      </p>
                    </div>
                  </div>
                  {selectedPlace ? (
                    <button className="button button-primary" type="button" onClick={openLocationCreate}>
                      <Plus size={17} aria-hidden="true" /> Ajouter un emplacement
                    </button>
                  ) : null}
                </div>

                {visibleLocations.length === 0 ? (
                  <EmptyState
                    icon={<MapPin size={27} aria-hidden="true" />}
                    title={`Aucun emplacement à ${selectedPlace?.nom ?? "ce lieu"}`}
                    message="Créez un premier repère pour pouvoir y ranger des bouteilles."
                    action={
                      selectedPlace ? (
                        <button className="button button-primary" type="button" onClick={openLocationCreate}>
                          <Plus size={18} aria-hidden="true" /> Ajouter un emplacement
                        </button>
                      ) : undefined
                    }
                  />
                ) : (
                  <div className="location-list">
                    {visibleLocations.map((location) => (
                      <LocationCard
                        key={location.id}
                        location={location}
                        onEdit={() => openLocationEdit(location)}
                        onDelete={() => setLocationToDelete(location)}
                      />
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            <OccupancySection groups={occupancyGroups} />
          </div>
        ) : null}
      </div>

      <PlaceEditorModal
        open={placeEditor !== null}
        place={placeEditor === "edit" ? placeToEdit : null}
        onClose={() => setPlaceEditor(null)}
        onSaved={async (place, editing) => {
          setPlaceEditor(null);
          await load(place.id);
          showToast({
            message: editing ? `« ${place.nom} » a été renommé.` : `« ${place.nom} » a été ajouté.`,
          });
        }}
      />
      {placeToDelete ? (
        <DeletePlaceModal
          open
          place={placeToDelete}
          destinations={places.filter((place) => place.id !== placeToDelete.id)}
          onClose={() => setPlaceToDelete(null)}
          onDeleted={async (destinationId) => {
            setPlaceToDelete(null);
            await load(destinationId ?? undefined);
            showToast({ message: "Le lieu a été supprimé.", tone: "info" });
          }}
        />
      ) : null}
      <LocationEditorModal
        open={locationEditor !== null}
        location={locationEditor === "edit" ? locationToEdit : null}
        places={places}
        defaultPlaceId={selectedPlace?.id ?? ""}
        onClose={() => setLocationEditor(null)}
        onSaved={async (location, editing) => {
          setLocationEditor(null);
          await load(location.lieu_id ?? undefined);
          showToast({
            message: editing
              ? `« ${location.libelle} » a été mis à jour.`
              : `« ${location.libelle} » est maintenant disponible.`,
          });
        }}
      />
      {locationToDelete ? (
        <DeleteLocationModal
          open
          location={locationToDelete}
          destinations={locations.filter((location) => location.id !== locationToDelete.id)}
          onClose={() => setLocationToDelete(null)}
          onDeleted={async () => {
            setLocationToDelete(null);
            await load(selectedPlaceId);
            showToast({ message: "L’emplacement a été supprimé.", tone: "info" });
          }}
        />
      ) : null}
    </AppShell>
  );
}

function OccupancySection({ groups }: { groups: CellarOccupancyGroup[] }) {
  return (
    <section className="occupancy-management" aria-labelledby="occupancy-title">
      <div className="management-heading">
        <div>
          <p className="management-step">3</p>
          <div>
            <h2 id="occupancy-title">Occupation</h2>
            <p>Bouteilles rangées, capacité et places libres par emplacement.</p>
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="occupancy-empty">Ajoutez un lieu et un emplacement pour suivre leur occupation.</p>
      ) : (
        <div className="occupancy-grid">
          {groups.map((group) => (
            <OccupancyCard key={group.id} group={group} />
          ))}
        </div>
      )}
    </section>
  );
}

function OccupancyCard({ group }: { group: CellarOccupancyGroup }) {
  const totalLabel =
    group.capacity == null
      ? `${bottleLabel(group.stock)}, capacité non renseignée`
      : `${bottleLabel(group.stock)} sur ${group.capacity} place${group.capacity === 1 ? "" : "s"}`;
  const availabilityTone =
    group.locations.length === 0 || group.capacity == null
      ? " is-unknown"
      : group.overflow > 0
        ? " over-capacity"
        : "";

  return (
    <article className={`occupancy-card${group.unassigned ? " occupancy-card-unassigned" : ""}`}>
      <header className="occupancy-card-heading">
        <div>
          <span className="occupancy-place-mark">
            {group.unassigned ? (
              <AlertTriangle size={18} aria-hidden="true" />
            ) : (
              <Gauge size={18} aria-hidden="true" />
            )}
          </span>
          <div>
            <h3>{group.name}</h3>
            <small>{group.locations.length} emplacement{group.locations.length === 1 ? "" : "s"}</small>
          </div>
        </div>
        <p className="occupancy-ratio">
          <span className="occupancy-ratio-visual" aria-hidden="true">
            <strong>{group.stock}</strong>
            <span>/ {group.capacity ?? "—"}</span>
            <WineIcon size={17} strokeWidth={1.8} />
          </span>
          <span className="sr-only">{totalLabel}</span>
        </p>
      </header>

      <p className={`occupancy-availability${availabilityTone}`}>
        {group.locations.length === 0
          ? "Aucun emplacement"
            : group.capacity == null
              ? "Capacité totale à compléter"
              : group.overflow > 0
                ? `${group.free && group.free > 0 ? `${group.free} place${group.free === 1 ? "" : "s"} libre${group.free === 1 ? "" : "s"} · ` : ""}${bottleLabel(group.overflow)} au-delà de la capacité`
              : `${group.free} place${group.free === 1 ? "" : "s"} libre${group.free === 1 ? "" : "s"}`}
      </p>

      {group.locations.length > 0 ? (
        <ul className="occupancy-location-list">
          {group.locations.map((location) => {
            const capacity = location.capacite_totale;
            const free = capacity == null ? null : Math.max(capacity - location.stock, 0);
            const overflow = capacity == null ? 0 : Math.max(location.stock - capacity, 0);
            const ratioLabel =
              capacity == null
                ? `${bottleLabel(location.stock)}, capacité non renseignée`
                : `${bottleLabel(location.stock)} sur ${capacity} place${capacity === 1 ? "" : "s"}`;

            return (
              <li key={location.id}>
                <span className="occupancy-location-name">
                  <MapPin size={15} aria-hidden="true" />
                  <span>
                    <strong>{location.nom}</strong>
                    <small>
                      {capacity == null
                        ? "Capacité à renseigner"
                        : overflow > 0
                          ? `${bottleLabel(overflow)} au-delà`
                          : `${free} place${free === 1 ? "" : "s"} libre${free === 1 ? "" : "s"}`}
                    </small>
                  </span>
                </span>
                <span className="occupancy-location-ratio">
                  <span className="occupancy-location-ratio-visual" aria-hidden="true">
                    <strong>{location.stock}</strong>
                    <span>/ {capacity ?? "—"}</span>
                    <WineIcon size={15} strokeWidth={1.8} />
                  </span>
                  <span className="sr-only">{ratioLabel}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
}

function LocationCard({
  location,
  onEdit,
  onDelete,
}: {
  location: CellarLocation;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <article className="location-card">
      <div className="location-identity">
        <span className="location-pin"><MapPin size={19} aria-hidden="true" /></span>
        <div>
          <h3>{location.nom}</h3>
          <p>{location.libelle}</p>
        </div>
      </div>
      <div className="location-counts" aria-label="Contenu de l’emplacement">
        <span><Grape size={16} aria-hidden="true" /><strong>{location.nombre_fiches}</strong> fiche{location.nombre_fiches === 1 ? "" : "s"}</span>
        <span><WineIcon size={16} aria-hidden="true" /><strong>{location.stock}</strong> bouteille{location.stock === 1 ? "" : "s"}</span>
      </div>
      <div className="location-actions">
        <button className="button button-ghost" type="button" onClick={onEdit}>
          <Pencil size={16} aria-hidden="true" /> Modifier
        </button>
        <button className="icon-button location-delete-button" type="button" onClick={onDelete} aria-label={`Supprimer ${location.libelle}`}>
          <Trash2 size={17} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

function PlaceEditorModal({
  open,
  place,
  onClose,
  onSaved,
}: {
  open: boolean;
  place: CellarPlaceRecord | null;
  onClose: () => void;
  onSaved: (place: CellarPlaceRecord, editing: boolean) => Promise<void>;
}) {
  const editing = Boolean(place);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(place?.nom ?? "");
    setError("");
  }, [open, place]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedName = name.trim().replace(/\s+/g, " ");
    if (!normalizedName) {
      setError("Le nom du lieu est obligatoire.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = place
        ? place.date_modification
          ? await api.updatePlace(place.id, normalizedName, place.date_modification)
          : await api.updatePlace(place.id, normalizedName)
        : await api.createPlace(normalizedName);
      await onSaved(saved, editing);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Renommer le lieu" : "Ajouter un lieu"}
      description={
        editing
          ? "Tous ses emplacements et toutes ses fiches afficheront automatiquement le nouveau nom."
          : "Le lieu sera proposé dans les emplacements, les filtres et le conseil d’accord."
      }
      size="small"
    >
      <form onSubmit={submit}>
        <label className="field">
          <span>Nom du lieu <b>*</b></span>
          <input value={name} onChange={(event) => setName(event.target.value)} required maxLength={120} placeholder="Ex. Paris" autoFocus />
        </label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions">
          <button className="button button-ghost" type="button" onClick={onClose}>Annuler</button>
          <button className="button button-primary" type="submit" disabled={saving}>
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeletePlaceModal({
  open,
  place,
  destinations,
  onClose,
  onDeleted,
}: {
  open: boolean;
  place: CellarPlaceRecord;
  destinations: CellarPlaceRecord[];
  onClose: () => void;
  onDeleted: (destinationId: string | null) => Promise<void>;
}) {
  const occupied = place.nombre_emplacements > 0 || place.nombre_fiches > 0;
  const [destination, setDestination] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDestination("");
    setError("");
  }, [open, place.id]);

  const remove = async () => {
    if (occupied && !destination) {
      setError("Choisissez un lieu de destination avant de poursuivre.");
      return;
    }
    setDeleting(true);
    setError("");
    try {
      if (place.date_modification) {
        await api.deletePlace(place.id, destination || null, place.date_modification);
      } else {
        await api.deletePlace(place.id, destination || null);
      }
      await onDeleted(destination || null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Suppression impossible.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Supprimer ${place.nom} ?`} description="La suppression du lieu est définitive." size="small">
      <div className="delete-warning">
        <AlertTriangle size={22} aria-hidden="true" />
        <p>
          <strong>{place.nom}</strong> contient <strong>{place.nombre_emplacements} emplacement{place.nombre_emplacements === 1 ? "" : "s"}</strong>, <strong>{place.nombre_fiches} fiche{place.nombre_fiches === 1 ? "" : "s"}</strong> et <strong>{bottleLabel(place.stock)}</strong>.
        </p>
      </div>
      {occupied ? (
        destinations.length ? (
          <label className="field deletion-destination">
            <span>Tout réaffecter au lieu <b>*</b></span>
            <select value={destination} onChange={(event) => { setDestination(event.target.value); setError(""); }} required>
              <option value="">Choisir un lieu…</option>
              {destinations.map((item) => <option key={item.id} value={item.id}>{item.nom}</option>)}
            </select>
            <small>Tous les emplacements et leurs bouteilles seront rattachés à ce lieu.</small>
          </label>
        ) : (
          <p className="form-error" role="alert">Ajoutez d’abord un autre lieu pour pouvoir réaffecter son contenu.</p>
        )
      ) : (
        <p className="empty-location-confirmation">Ce lieu est vide : aucun emplacement ni aucune fiche ne sera déplacé.</p>
      )}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      <div className="modal-actions">
        <button className="button button-ghost" type="button" onClick={onClose}>Conserver</button>
        <button className="button button-danger" type="button" disabled={deleting || (occupied && (!destination || destinations.length === 0))} onClick={() => void remove()}>
          <Trash2 size={17} aria-hidden="true" /> {deleting ? "Suppression…" : occupied ? "Réaffecter et supprimer" : "Supprimer"}
        </button>
      </div>
    </Modal>
  );
}

export function LocationEditorModal({
  open,
  location,
  places,
  defaultPlaceId,
  onClose,
  onSaved,
}: {
  open: boolean;
  location: CellarLocation | null;
  places: CellarPlaceRecord[];
  defaultPlaceId: string;
  onClose: () => void;
  onSaved: (location: CellarLocation, editing: boolean) => Promise<void>;
}) {
  const editing = Boolean(location);
  const [placeId, setPlaceId] = useState("");
  const [name, setName] = useState("");
  const [totalCapacity, setTotalCapacity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPlaceId(location?.lieu_id ?? defaultPlaceId ?? places[0]?.id ?? "");
    setName(location?.nom ?? "");
    setTotalCapacity(
      location?.capacite_totale == null ? "" : String(location.capacite_totale),
    );
    setError("");
  }, [defaultPlaceId, location, open, places]);

  const selectedPlace = places.find((place) => place.id === placeId);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedName = name.trim().replace(/\s+/g, " ");
    const capacity = totalCapacity.trim() === "" ? null : Number(totalCapacity);
    if (!placeId) {
      setError("Choisissez le lieu de cet emplacement.");
      return;
    }
    if (!normalizedName) {
      setError("Le nom de l’emplacement est obligatoire.");
      return;
    }
    if (
      capacity !== null &&
      (!Number.isSafeInteger(capacity) || capacity < 0)
    ) {
      setError("Le nombre total de places doit être un entier positif ou nul.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fields = {
        lieu_id: placeId,
        nom: normalizedName,
        capacite_totale: capacity,
      };
      const saved = location
        ? location.date_modification
          ? await api.updateLocation(location.id, fields, location.date_modification)
          : await api.updateLocation(location.id, fields)
        : await api.createLocation({
            ...fields,
          });
      await onSaved(saved, editing);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Modifier l’emplacement" : "Ajouter un emplacement"}
      description={
        editing && location
          ? `${location.nombre_fiches} fiche${location.nombre_fiches === 1 ? " sera mise à jour" : "s seront mises à jour"} avec ce nouveau libellé.`
          : "Le nouvel emplacement sera immédiatement disponible dans les fiches de vin."
      }
      size="small"
    >
      <form onSubmit={submit}>
        <label className="field">
          <span>Lieu <b>*</b></span>
          <select value={placeId} onChange={(event) => setPlaceId(event.target.value)} required autoFocus>
            <option value="">Choisir un lieu…</option>
            {places.map((place) => <option key={place.id} value={place.id}>{place.nom}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Nom de l’emplacement <b>*</b></span>
          <input value={name} onChange={(event) => setName(event.target.value)} required maxLength={120} placeholder="Ex. cave électrique" />
        </label>
        <label className="field">
          <span>Nombre total de places <em>facultatif</em></span>
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={totalCapacity}
            onChange={(event) => setTotalCapacity(event.target.value)}
            placeholder="Ex. 48"
          />
          <small>Laissez vide si la capacité n’est pas encore connue.</small>
        </label>
        <p className="location-preview">
          <span>Libellé affiché</span>
          <strong>{selectedPlace?.nom ?? "Lieu"} - {name.trim().replace(/\s+/g, " ") || "…"}</strong>
        </p>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions">
          <button className="button button-ghost" type="button" onClick={onClose}>Annuler</button>
          <button className="button button-primary" type="submit" disabled={saving || places.length === 0}>{saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}</button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteLocationModal({
  open,
  location,
  destinations,
  onClose,
  onDeleted,
}: {
  open: boolean;
  location: CellarLocation;
  destinations: CellarLocation[];
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const occupied = location.nombre_fiches > 0;
  const [destination, setDestination] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setDestination("");
      setError("");
    }
  }, [open, location.id]);

  const remove = async () => {
    if (occupied && !destination) {
      setError("Choisissez où déplacer les fiches avant de supprimer l’emplacement.");
      return;
    }
    setDeleting(true);
    setError("");
    try {
      const destinationId =
        destination === "__without_location__" || !occupied ? null : destination;
      if (location.date_modification) {
        await api.deleteLocation(
          location.id,
          destinationId,
          location.date_modification,
        );
      } else {
        await api.deleteLocation(location.id, destinationId);
      }
      await onDeleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Suppression impossible.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Supprimer cet emplacement ?" description="La suppression est définitive." size="small">
      <div className="delete-warning">
        <AlertTriangle size={22} aria-hidden="true" />
        <p>
          <strong>{location.libelle}</strong> contient <strong>{location.nombre_fiches} fiche{location.nombre_fiches === 1 ? "" : "s"}</strong> et <strong>{bottleLabel(location.stock)}</strong>.
        </p>
      </div>
      {occupied ? (
        <label className="field deletion-destination">
          <span>Déplacer toutes les fiches vers <b>*</b></span>
          <LocationSelect
            locations={destinations}
            value={destination}
            onChange={setDestination}
            emptyLabel="Sans emplacement"
            emptyValue="__without_location__"
            placeholder="Choisir une destination…"
          />
          <small>Cette réaffectation est obligatoire et concerne toutes les fiches ci-dessus.</small>
        </label>
      ) : (
        <p className="empty-location-confirmation">Cet emplacement est vide : aucune fiche ne sera déplacée.</p>
      )}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      <div className="modal-actions">
        <button className="button button-ghost" type="button" onClick={onClose}>Conserver</button>
        <button className="button button-danger" type="button" disabled={deleting || (occupied && !destination)} onClick={() => void remove()}>
          <Trash2 size={17} aria-hidden="true" /> {deleting ? "Suppression…" : occupied ? "Déplacer et supprimer" : "Supprimer"}
        </button>
      </div>
    </Modal>
  );
}
